import { HttpErrorResponse } from '@angular/common/http';

import { GuestSelectors, UserSelectors } from '@recruitee/account-data';
import { CandidateSelectors } from '@recruitee/candidate-data';
import type {
  InterviewResult,
  InterviewResultMeta,
  InterviewResultResponseSingle,
  InterviewResultsReferences,
  Params,
} from '@recruitee/interview-results-types';
import {
  CorrelationId,
  createEntityCollectionStore,
  DEFAULT_SCOPE_NAME,
  EntityState,
  getEntityCollectionActions,
  objectToScope,
  pipeState,
} from '@recruitee/ngrx';
import { JobsBriefSelectors, StageSelectors } from '@recruitee/offers-data';
import { NewTalentPoolsBriefSelectors } from '@recruitee/talent-pools-data';

type State = EntityState<InterviewResult, { scopeMeta: InterviewResultMeta }>;

const idSelector = ({ id }: { id: number | string }) => id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<
  InterviewResult,
  { scopeMeta: InterviewResultMeta }
>()('InterviewResults', {
  idSelector,
  references: makeRef => [
    makeRef('guestId', 'guest', GuestSelectors.byId),
    makeRef('adminId', 'admin', UserSelectors.byId),
    makeRef('candidateId', 'candidate', CandidateSelectors.byId),
    makeRef('offerId', 'offer', offerId => JobsBriefSelectors.byId(offerId!)!),
    makeRef('talentPoolId', 'talentPool', talentPoolId =>
      NewTalentPoolsBriefSelectors.byId(talentPoolId),
    ),
    makeRef('stageId', 'stage', StageSelectors.byId),
  ],

  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => {
    const defaultActions = getEntityCollectionActions(adapter, idSelector);

    const fetchCollection = (
      state: State,
      payload: { params: { scope: string } & Params; replace?: boolean },
    ) =>
      pipeState(
        state,
        next => (payload.replace ? adapter.resetAll(next) : next),
        next =>
          adapter.markAsPending(next, {
            scope: objectToScope(payload.params),
            entities: [],
          }),
      );
    const fetchCollectionSuccess = (
      state: State,
      payload: {
        data: InterviewResult[];
        meta: InterviewResultMeta;
        references: InterviewResultsReferences[];
        scope: string;
        replace?: boolean;
      },
    ) =>
      pipeState(
        state,
        next => adapter.setScopeMetaCustom(next, payload.meta, { scope: payload.scope }),
        next => (payload.replace ? adapter.setMany(next, [], { scope: payload.scope }) : next),
        next => adapter.setMany(next, payload.data, { scope: payload.scope }),
        next =>
          adapter.markAsLoaded(next, {
            scope: payload.scope || DEFAULT_SCOPE_NAME,
            entities: payload.data.map(e => e.id),
          }),
      );

    return {
      fetchCollection,
      fetchCollectionSuccess: fetchCollectionSuccess,

      fetchCollectionBuffered: fetchCollection,
      fetchCollectionBufferedSuccess: fetchCollectionSuccess,
      fetchCollectionBufferedFailed: defaultActions.fetchCollectionFailed,

      fetchItem: (state, payload: { id: number; scope?: string }) => adapter.markAsPending(state),
      fetchItemSuccess: (state, payload: { data: InterviewResultResponseSingle; scope?: string }) =>
        pipeState(
          state,
          next =>
            adapter.addOne(next, payload.data.interviewResult, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
            }),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [payload.data.interviewResult.id],
            }),
        ),
      fetchItemFailed: (state, payload) => defaultActions.fetchOneFailed(state, payload),
      createOnePessimistic: (
        state,
        payload: {
          data: { value: Partial<InterviewResult>; candidateId: number };
          scope?: string;
        } & Required<CorrelationId>,
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),
      createOnePessimisticSuccess: (
        state,
        payload: { data: InterviewResult; scope?: string } & Required<CorrelationId>,
      ) =>
        pipeState(
          state,
          next => adapter.addOne(next, payload.data),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      createOnePessimisticFailed: (state, payload: HttpErrorResponse) =>
        adapter.setError(state, payload),

      update: (state, payload: { data: { id: number; value: any }; scope?: string }) =>
        adapter.markAsPending(state),
      updateSuccess: (state, payload: { data: InterviewResult; scope?: string }) =>
        pipeState(
          state,
          next => adapter.updateOne(next, payload.data),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      updateFailed: (state, payload: HttpErrorResponse) => adapter.setError(state, payload),

      deleteOnePessimistic: (
        state,
        payload: {
          data: { id: number };
          scope?: string;
        } & Required<CorrelationId>,
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),
      deleteOnePessimisticSuccess: (
        state,
        payload: { data: InterviewResult; scope?: string } & Required<CorrelationId>,
      ) =>
        pipeState(
          state,
          next => adapter.addOne(next, payload.data),
          next =>
            adapter.markAsLoaded(next, {
              scope: payload?.scope || DEFAULT_SCOPE_NAME,
              entities: [],
            }),
        ),
      deleteOnePessimisticFailed: (state, payload: HttpErrorResponse) =>
        adapter.setError(state, payload),
    };
  },
});

export const interviewResultsCandidateScope = (candidateId: number) => `candidate_${candidateId}`;

export {
  actions as InterviewResultsActions,
  selectors as InterviewResultsSelectors,
  reducer as InterviewResultsReducer,
  namespace as InterviewResultsNamespace,
};
